// Bluetooth Device Manager for Medical Devices
// Uses Web Bluetooth API for device discovery and connection

declare global {
    interface Navigator {
        bluetooth: Bluetooth;
    }
    
    interface Bluetooth {
        requestDevice(options: RequestDeviceOptions): Promise<BluetoothDevice>;
        getAvailability(): Promise<boolean>;
    }
    
    interface BluetoothDevice extends EventTarget {
        id: string;
        name?: string;
        gatt?: BluetoothRemoteGATTServer;
    }
    
    interface BluetoothRemoteGATTServer {
        connect(): Promise<BluetoothRemoteGATTServer>;
        disconnect(): void;
        connected: boolean;
        getPrimaryService(service: string | number): Promise<BluetoothRemoteGATTService>;
        getPrimaryServices(service?: string | number): Promise<BluetoothRemoteGATTService[]>;
    }
    
    interface BluetoothRemoteGATTService {
        uuid: string;
        getCharacteristic(characteristic: string | number): Promise<BluetoothRemoteGATTCharacteristic>;
        getCharacteristics(characteristic?: string | number): Promise<BluetoothRemoteGATTCharacteristic[]>;
    }
    
    interface BluetoothRemoteGATTCharacteristic {
        uuid: string;
        properties: BluetoothCharacteristicProperties;
        readValue(): Promise<DataView>;
        writeValue(value: BufferSource): Promise<void>;
        startNotifications(): Promise<BluetoothRemoteGATTCharacteristic>;
        stopNotifications(): Promise<BluetoothRemoteGATTCharacteristic>;
    }
    
    interface BluetoothCharacteristicProperties {
        read: boolean;
        write: boolean;
        notify: boolean;
        indicate: boolean;
    }
    
    interface RequestDeviceOptions {
        filters?: Array<{
            services?: Array<string | number>;
            name?: string;
            namePrefix?: string;
            manufacturerData?: Array<{
                companyIdentifier: number;
                dataPrefix?: BufferSource;
                mask?: BufferSource;
            }>;
            serviceData?: Array<{
                service: string | number;
                dataPrefix?: BufferSource;
                mask?: BufferSource;
            }>;
        }>;
        optionalServices?: Array<string | number>;
        acceptAllDevices?: boolean;
    }
}

export interface BluetoothDeviceInfo {
    id: string;
    name: string;
    address?: string;
    deviceType: MedicalDeviceType;
    manufacturer?: string;
    isConnected: boolean;
    isPaired: boolean;
    rssi?: number;
    deviceClass?: number[];
    services?: string[];
}

export interface BluetoothReading {
    timestamp: number;
    deviceId: string;
    readings: {
        name: string;
        value: number;
        unit: string;
    }[];
    isAlert: boolean;
    alertLevel: number; // 0=normal, 1=warning, 2=critical
    alertMessage?: string;
}

export enum MedicalDeviceType {
    UNKNOWN = 'unknown',
    BLOOD_PRESSURE = 'blood_pressure',
    GLUCOMETER = 'glucometer',
    PULSE_OXIMETER = 'pulse_oximeter',
    ECG = 'ecg',
    THERMOMETER = 'thermometer',
    WEIGHT_SCALE = 'weight_scale',
    SPIROMETER = 'spirometer',
    HEART_RATE_MONITOR = 'heart_rate_monitor',
    FITNESS_TRACKER = 'fitness_tracker'
}

export enum ConnectionStatus {
    DISCONNECTED = 'disconnected',
    CONNECTING = 'connecting',
    CONNECTED = 'connected',
    DISCOVERING = 'discovering',
    ERROR = 'error'
}

// Medical device service UUIDs (commented out as not currently used)
// const MEDICAL_SERVICES = {
//     HEALTH_THERMOMETER: 'health_thermometer',
//     HEART_RATE: 'heart_rate',
//     BLOOD_PRESSURE: 'blood_pressure',
//     GLUCOSE: 'glucose',
//     WEIGHT_SCALE: 'weight_scale',
//     PULSE_OXIMETER: 'pulse_oximeter',
//     BODY_COMPOSITION: 'body_composition',
//     CYCLING_POWER: 'cycling_power',
//     CYCLING_SPEED_AND_CADENCE: 'cycling_speed_and_cadence',
//     LOCATION_AND_NAVIGATION: 'location_and_navigation',
//     FITNESS_TRACKER: 'fitness_tracker'
// };

// Service UUIDs for different medical devices
const SERVICE_UUIDS = {
    HEALTH_THERMOMETER: 0x1809,
    HEART_RATE: 0x180D,
    BLOOD_PRESSURE: 0x1810,
    GLUCOSE: 0x1808,
    WEIGHT_SCALE: 0x181D,
    PULSE_OXIMETER: 0x1822,
    BODY_COMPOSITION: 0x181B,
    CYCLING_POWER: 0x1818,
    CYCLING_SPEED_AND_CADENCE: 0x1816,
    LOCATION_AND_NAVIGATION: 0x1819,
    FITNESS_TRACKER: 0x1826
};

// Characteristic UUIDs for reading data
const CHARACTERISTIC_UUIDS = {
    TEMPERATURE_MEASUREMENT: 0x2A1C,
    HEART_RATE_MEASUREMENT: 0x2A37,
    BLOOD_PRESSURE_MEASUREMENT: 0x2A35,
    GLUCOSE_MEASUREMENT: 0x2A18,
    WEIGHT_MEASUREMENT: 0x2A9D,
    PULSE_OXIMETER_MEASUREMENT: 0x2A5F,
    BODY_COMPOSITION_MEASUREMENT: 0x2A9C
};

export class BluetoothDeviceManager {
    private devices: Map<string, BluetoothDeviceInfo> = new Map();
    private connectedDevices: Map<string, BluetoothDevice> = new Map();
    private status: ConnectionStatus = ConnectionStatus.DISCONNECTED;
    private isDiscovering: boolean = false;
    private eventListeners: Map<string, Function[]> = new Map();

    constructor() {
        this.checkBluetoothSupport();
    }

    /**
     * Check if Web Bluetooth API is supported
     */
    private checkBluetoothSupport(): boolean {
        if (!navigator.bluetooth) {
            console.error('Web Bluetooth API is not supported in this browser');
            this.emit('error', new Error('Web Bluetooth API not supported'));
            return false;
        }
        return true;
    }

    /**
     * Start Bluetooth device discovery
     */
    async startDiscovery(): Promise<BluetoothDeviceInfo[]> {
        if (!this.checkBluetoothSupport()) {
            throw new Error('Bluetooth not supported');
        }

        if (this.isDiscovering) {
            throw new Error('Discovery already in progress');
        }

        try {
            this.isDiscovering = true;
            this.status = ConnectionStatus.DISCOVERING;
            this.emit('statusChanged', this.status);

            // Request Bluetooth device with medical service filters
            const device = await navigator.bluetooth.requestDevice({
                acceptAllDevices: true, // Accept all devices initially
                optionalServices: Object.values(SERVICE_UUIDS).map(uuid => 
                    typeof uuid === 'number' ? `0000${uuid.toString(16).padStart(4, '0')}-0000-1000-8000-00805f9b34fb` : uuid
                )
            });

            // Process discovered device
            const deviceInfo = await this.processDiscoveredDevice(device);
            this.devices.set(deviceInfo.id, deviceInfo);
            
            this.emit('deviceDiscovered', deviceInfo);
            this.emit('devicesUpdated', Array.from(this.devices.values()));

            return [deviceInfo];

        } catch (error) {
            console.error('Bluetooth discovery failed:', error);
            this.emit('error', error);
            throw error;
        } finally {
            this.isDiscovering = false;
            this.status = ConnectionStatus.DISCONNECTED;
            this.emit('statusChanged', this.status);
        }
    }

    /**
     * Process a discovered Bluetooth device
     */
    private async processDiscoveredDevice(device: BluetoothDevice): Promise<BluetoothDeviceInfo> {
        const deviceInfo: BluetoothDeviceInfo = {
            id: device.id,
            name: device.name || 'Unknown Device',
            deviceType: this.identifyDeviceType(device),
            isConnected: device.gatt?.connected || false,
            isPaired: false // Web Bluetooth doesn't provide pairing status
        };

        // Try to get additional device information
        if (device.gatt?.connected) {
            try {
                const services = await device.gatt.getPrimaryServices();
                deviceInfo.services = services.map((service: BluetoothRemoteGATTService) => service.uuid);
            } catch (error) {
                console.warn('Could not get device services:', error);
            }
        }

        return deviceInfo;
    }

    /**
     * Identify the type of medical device based on name and services
     */
    private identifyDeviceType(device: BluetoothDevice): MedicalDeviceType {
        const name = device.name?.toLowerCase() || '';
        
        // Blood pressure monitors
        if (name.includes('blood') || name.includes('pressure') || name.includes('bp') || 
            name.includes('omron') || name.includes('hem')) {
            return MedicalDeviceType.BLOOD_PRESSURE;
        }
        
        // Glucometers
        if (name.includes('glucose') || name.includes('sugar') || name.includes('diabetes') ||
            name.includes('freestyle') || name.includes('accu-chek')) {
            return MedicalDeviceType.GLUCOMETER;
        }
        
        // Pulse oximeters
        if (name.includes('pulse') || name.includes('oximeter') || name.includes('spo2') ||
            name.includes('oxygen')) {
            return MedicalDeviceType.PULSE_OXIMETER;
        }
        
        // Heart rate monitors
        if (name.includes('heart') || name.includes('hr') || name.includes('cardio') ||
            name.includes('polar') || name.includes('garmin')) {
            return MedicalDeviceType.HEART_RATE_MONITOR;
        }
        
        // Thermometers
        if (name.includes('temp') || name.includes('thermo') || name.includes('fever')) {
            return MedicalDeviceType.THERMOMETER;
        }
        
        // Weight scales
        if (name.includes('weight') || name.includes('scale') || name.includes('body') ||
            name.includes('fitbit') || name.includes('withings')) {
            return MedicalDeviceType.WEIGHT_SCALE;
        }
        
        // Fitness trackers
        if (name.includes('fitness') || name.includes('tracker') || name.includes('band') ||
            name.includes('watch') || name.includes('smart')) {
            return MedicalDeviceType.FITNESS_TRACKER;
        }
        
        return MedicalDeviceType.UNKNOWN;
    }

    /**
     * Connect to a specific Bluetooth device
     */
    async connectToDevice(deviceId: string): Promise<boolean> {
        const deviceInfo = this.devices.get(deviceId);
        if (!deviceInfo) {
            throw new Error('Device not found');
        }

        if (deviceInfo.isConnected) {
            return true; // Already connected
        }

        try {
            this.status = ConnectionStatus.CONNECTING;
            this.emit('statusChanged', this.status);

            // Request connection to the specific device
            const device = await navigator.bluetooth.requestDevice({
                acceptAllDevices: false,
                filters: [
                    { services: [`0000${SERVICE_UUIDS.HEART_RATE.toString(16).padStart(4, '0')}-0000-1000-8000-00805f9b34fb`] },
                    { services: [`0000${SERVICE_UUIDS.HEALTH_THERMOMETER.toString(16).padStart(4, '0')}-0000-1000-8000-00805f9b34fb`] },
                    { services: [`0000${SERVICE_UUIDS.BLOOD_PRESSURE.toString(16).padStart(4, '0')}-0000-1000-8000-00805f9b34fb`] },
                    { services: [`0000${SERVICE_UUIDS.GLUCOSE.toString(16).padStart(4, '0')}-0000-1000-8000-00805f9b34fb`] },
                    { services: [`0000${SERVICE_UUIDS.WEIGHT_SCALE.toString(16).padStart(4, '0')}-0000-1000-8000-00805f9b34fb`] },
                    { services: [`0000${SERVICE_UUIDS.PULSE_OXIMETER.toString(16).padStart(4, '0')}-0000-1000-8000-00805f9b34fb`] }
                ],
                optionalServices: Object.values(SERVICE_UUIDS).map(uuid => 
                    typeof uuid === 'number' ? `0000${uuid.toString(16).padStart(4, '0')}-0000-1000-8000-00805f9b34fb` : uuid
                )
            });

            // Connect to GATT server
            const server = await device.gatt!.connect();
            
            // Store connected device
            this.connectedDevices.set(deviceId, device);
            
            // Update device info
            deviceInfo.isConnected = true;
            this.devices.set(deviceId, deviceInfo);
            
            this.status = ConnectionStatus.CONNECTED;
            this.emit('statusChanged', this.status);
            this.emit('deviceConnected', deviceInfo);
            this.emit('devicesUpdated', Array.from(this.devices.values()));

            return true;

        } catch (error) {
            console.error('Failed to connect to device:', error);
            this.status = ConnectionStatus.ERROR;
            this.emit('statusChanged', this.status);
            this.emit('error', error);
            throw error;
        }
    }

    /**
     * Disconnect from a Bluetooth device
     */
    async disconnectDevice(deviceId: string): Promise<boolean> {
        const device = this.connectedDevices.get(deviceId);
        const deviceInfo = this.devices.get(deviceId);

        if (!device || !deviceInfo) {
            return false;
        }

        try {
            if (device.gatt?.connected) {
                await device.gatt.disconnect();
            }
            
            this.connectedDevices.delete(deviceId);
            deviceInfo.isConnected = false;
            this.devices.set(deviceId, deviceInfo);
            
            this.emit('deviceDisconnected', deviceInfo);
            this.emit('devicesUpdated', Array.from(this.devices.values()));
            
            return true;

        } catch (error) {
            console.error('Failed to disconnect device:', error);
            this.emit('error', error);
            throw error;
        }
    }

    /**
     * Read data from a connected device
     */
    async readDeviceData(deviceId: string): Promise<BluetoothReading> {
        const device = this.connectedDevices.get(deviceId);
        const deviceInfo = this.devices.get(deviceId);

        if (!device || !deviceInfo || !device.gatt?.connected) {
            throw new Error('Device not connected');
        }

        try {
            const server = await device.gatt.connect();
            const readings: { name: string; value: number; unit: string }[] = [];

            // Read data based on device type
            switch (deviceInfo.deviceType) {
                case MedicalDeviceType.HEART_RATE_MONITOR:
                    readings.push(...await this.readHeartRateData(server));
                    break;
                case MedicalDeviceType.THERMOMETER:
                    readings.push(...await this.readTemperatureData(server));
                    break;
                case MedicalDeviceType.BLOOD_PRESSURE:
                    readings.push(...await this.readBloodPressureData(server));
                    break;
                case MedicalDeviceType.GLUCOMETER:
                    readings.push(...await this.readGlucoseData(server));
                    break;
                case MedicalDeviceType.WEIGHT_SCALE:
                    readings.push(...await this.readWeightData(server));
                    break;
                case MedicalDeviceType.PULSE_OXIMETER:
                    readings.push(...await this.readPulseOximeterData(server));
                    break;
                default:
                    // Try to read generic data
                    readings.push(...await this.readGenericData(server));
                    break;
            }

            const reading: BluetoothReading = {
                timestamp: Date.now(),
                deviceId,
                readings,
                isAlert: false,
                alertLevel: 0
            };

            this.emit('dataReceived', reading);
            return reading;

        } catch (error) {
            console.error('Failed to read device data:', error);
            this.emit('error', error);
            throw error;
        }
    }

    /**
     * Read heart rate data
     */
    private async readHeartRateData(server: BluetoothRemoteGATTServer): Promise<{ name: string; value: number; unit: string }[]> {
        const service = await server.getPrimaryService(`0000${SERVICE_UUIDS.HEART_RATE.toString(16).padStart(4, '0')}-0000-1000-8000-00805f9b34fb`);
        const characteristic = await service.getCharacteristic(`0000${CHARACTERISTIC_UUIDS.HEART_RATE_MEASUREMENT.toString(16).padStart(4, '0')}-0000-1000-8000-00805f9b34fb`);
        const value = await characteristic.readValue();
        
        // Parse heart rate data (simplified)
        const heartRate = value.getUint8(1);
        return [{ name: 'Heart Rate', value: heartRate, unit: 'bpm' }];
    }

    /**
     * Read temperature data
     */
    private async readTemperatureData(server: BluetoothRemoteGATTServer): Promise<{ name: string; value: number; unit: string }[]> {
        const service = await server.getPrimaryService(`0000${SERVICE_UUIDS.HEALTH_THERMOMETER.toString(16).padStart(4, '0')}-0000-1000-8000-00805f9b34fb`);
        const characteristic = await service.getCharacteristic(`0000${CHARACTERISTIC_UUIDS.TEMPERATURE_MEASUREMENT.toString(16).padStart(4, '0')}-0000-1000-8000-00805f9b34fb`);
        const value = await characteristic.readValue();
        
        // Parse temperature data (simplified)
        const temp = value.getInt16(1, true) / 100; // Convert from centidegrees
        return [{ name: 'Temperature', value: temp, unit: '°C' }];
    }

    /**
     * Read blood pressure data
     */
    private async readBloodPressureData(server: BluetoothRemoteGATTServer): Promise<{ name: string; value: number; unit: string }[]> {
        const service = await server.getPrimaryService(`0000${SERVICE_UUIDS.BLOOD_PRESSURE.toString(16).padStart(4, '0')}-0000-1000-8000-00805f9b34fb`);
        const characteristic = await service.getCharacteristic(`0000${CHARACTERISTIC_UUIDS.BLOOD_PRESSURE_MEASUREMENT.toString(16).padStart(4, '0')}-0000-1000-8000-00805f9b34fb`);
        const value = await characteristic.readValue();
        
        // Parse blood pressure data (simplified)
        const systolic = value.getUint16(1, true);
        const diastolic = value.getUint16(3, true);
        
        return [
            { name: 'Systolic Pressure', value: systolic, unit: 'mmHg' },
            { name: 'Diastolic Pressure', value: diastolic, unit: 'mmHg' }
        ];
    }

    /**
     * Read glucose data
     */
    private async readGlucoseData(server: BluetoothRemoteGATTServer): Promise<{ name: string; value: number; unit: string }[]> {
        const service = await server.getPrimaryService(`0000${SERVICE_UUIDS.GLUCOSE.toString(16).padStart(4, '0')}-0000-1000-8000-00805f9b34fb`);
        const characteristic = await service.getCharacteristic(`0000${CHARACTERISTIC_UUIDS.GLUCOSE_MEASUREMENT.toString(16).padStart(4, '0')}-0000-1000-8000-00805f9b34fb`);
        const value = await characteristic.readValue();
        
        // Parse glucose data (simplified)
        const glucose = value.getUint16(1, true) / 1000; // Convert from mg/dL
        return [{ name: 'Blood Glucose', value: glucose, unit: 'mg/dL' }];
    }

    /**
     * Read weight data
     */
    private async readWeightData(server: BluetoothRemoteGATTServer): Promise<{ name: string; value: number; unit: string }[]> {
        const service = await server.getPrimaryService(`0000${SERVICE_UUIDS.WEIGHT_SCALE.toString(16).padStart(4, '0')}-0000-1000-8000-00805f9b34fb`);
        const characteristic = await service.getCharacteristic(`0000${CHARACTERISTIC_UUIDS.WEIGHT_MEASUREMENT.toString(16).padStart(4, '0')}-0000-1000-8000-00805f9b34fb`);
        const value = await characteristic.readValue();
        
        // Parse weight data (simplified)
        const weight = value.getUint16(1, true) / 100; // Convert from kg
        return [{ name: 'Weight', value: weight, unit: 'kg' }];
    }

    /**
     * Read pulse oximeter data
     */
    private async readPulseOximeterData(server: BluetoothRemoteGATTServer): Promise<{ name: string; value: number; unit: string }[]> {
        const service = await server.getPrimaryService(`0000${SERVICE_UUIDS.PULSE_OXIMETER.toString(16).padStart(4, '0')}-0000-1000-8000-00805f9b34fb`);
        const characteristic = await service.getCharacteristic(`0000${CHARACTERISTIC_UUIDS.PULSE_OXIMETER_MEASUREMENT.toString(16).padStart(4, '0')}-0000-1000-8000-00805f9b34fb`);
        const value = await characteristic.readValue();
        
        // Parse pulse oximeter data (simplified)
        const spo2 = value.getUint8(1);
        const heartRate = value.getUint8(2);
        
        return [
            { name: 'SpO2', value: spo2, unit: '%' },
            { name: 'Heart Rate', value: heartRate, unit: 'bpm' }
        ];
    }

    /**
     * Read generic data from any available service
     */
    private async readGenericData(server: BluetoothRemoteGATTServer): Promise<{ name: string; value: number; unit: string }[]> {
        const services = await server.getPrimaryServices();
        const readings: { name: string; value: number; unit: string }[] = [];

        for (const service of services) {
            try {
                const characteristics = await service.getCharacteristics();
                for (const characteristic of characteristics) {
                    if (characteristic.properties.read) {
                        const value = await characteristic.readValue();
                        readings.push({
                            name: `Service ${service.uuid}`,
                            value: value.getUint8(0),
                            unit: 'raw'
                        });
                    }
                }
            } catch (error) {
                console.warn('Could not read from service:', service.uuid, error);
            }
        }

        return readings;
    }

    /**
     * Get all discovered devices
     */
    getDevices(): BluetoothDeviceInfo[] {
        return Array.from(this.devices.values());
    }

    /**
     * Get connected devices
     */
    getConnectedDevices(): BluetoothDeviceInfo[] {
        return Array.from(this.devices.values()).filter(device => device.isConnected);
    }

    /**
     * Get current connection status
     */
    getStatus(): ConnectionStatus {
        return this.status;
    }

    /**
     * Check if discovery is in progress
     */
    isDiscoveryInProgress(): boolean {
        return this.isDiscovering;
    }

    /**
     * Event handling
     */
    on(event: string, callback: Function): void {
        if (!this.eventListeners.has(event)) {
            this.eventListeners.set(event, []);
        }
        this.eventListeners.get(event)!.push(callback);
    }

    off(event: string, callback: Function): void {
        const listeners = this.eventListeners.get(event);
        if (listeners) {
            const index = listeners.indexOf(callback);
            if (index > -1) {
                listeners.splice(index, 1);
            }
        }
    }

    private emit(event: string, data?: any): void {
        const listeners = this.eventListeners.get(event);
        if (listeners) {
            listeners.forEach(callback => {
                try {
                    callback(data);
                } catch (error) {
                    console.error('Error in event listener:', error);
                }
            });
        }
    }

    /**
     * Cleanup and disconnect all devices
     */
    async cleanup(): Promise<void> {
        const deviceIds = Array.from(this.connectedDevices.keys());
        for (const deviceId of deviceIds) {
            await this.disconnectDevice(deviceId);
        }
        
        this.devices.clear();
        this.connectedDevices.clear();
        this.status = ConnectionStatus.DISCONNECTED;
        this.emit('statusChanged', this.status);
    }
}

// Export singleton instance
export const bluetoothDeviceManager = new BluetoothDeviceManager();
